/**
 * Application wide event bus.
 *
 * Replaces the `$rootScope.$broadcast` / `$rootScope.$on` pair the app relied on while it was
 * running on angular. Written by hand rather than as `new Vue()` on purpose: vue 3 drops
 * `$on`/`$off`/`$emit` from component instances, so a vue based bus would have to be replaced
 * again.
 *
 * The per feature buses of the vue code are that same `EventBus` class now - each of them was
 * a `new Vue()` used for nothing but `$on`/`$off`/`$emit`, which is what retires the
 * INSTANCE_EVENT_EMITTER compat layer. They stay separate instances rather than folding into
 * this one: a bus per feature is what keeps `'close'` on one page from reaching another. Only
 * this one is shared through `window`, because only this one has to cross bundles.
 *
 * The instance is stashed on `window` because a module level instance is per entry: the two
 * entries in rspack.config.mjs would each get their own bus, and the site header would never
 * hear what the page broadcasts.
 */

const GLOBAL_KEY = '__mmGlobalEventBus__';

class EventBus {
    constructor () {
        this.listeners = new Map();
    }

    /**
     * @param eventName {String}
     * @param handler {Function} called with the payload the event was emitted with
     * @returns {Function} unsubscribe
     */
    on (eventName, handler) {
        let handlers = this.listeners.get(eventName);
        if (!handlers) {
            handlers = new Set();
            this.listeners.set(eventName, handlers);
        }
        handlers.add(handler);
        return () => this.off(eventName, handler);
    }

    once (eventName, handler) {
        const unsubscribe = this.on(eventName, (...args) => {
            unsubscribe();
            handler(...args);
        });
        return unsubscribe;
    }

    /**
     * @param eventName {String}
     * @param handler {Function} [handler] omit to drop every handler of the event, the way
     *                 vue 2's `$off(event)` did - the per feature buses were `new Vue()` and
     *                 several of them unsubscribe that way
     */
    off (eventName, handler) {
        const handlers = this.listeners.get(eventName);
        if (!handlers) {
            return;
        }
        if (handler === undefined) {
            this.listeners.delete(eventName);
            return;
        }
        handlers.delete(handler);
        if (!handlers.size) {
            this.listeners.delete(eventName);
        }
    }

    emit (eventName, ...args) {
        const handlers = this.listeners.get(eventName);
        if (!handlers) {
            return;
        }
        // a copy, so that a handler unsubscribing itself does not skip the next one
        for (const handler of [...handlers]) {
            handler(...args);
        }
    }

    /**
     * Subscribes to several events at once.
     *
     * @param listeners {Object} map of event name -> handler
     * @returns {Function} unsubscribe from all of them
     */
    subscribe (listeners) {
        const unsubscribers = Object.entries(listeners).map(([eventName, handler]) => this.on(eventName, handler));
        return () => unsubscribers.forEach(unsubscribe => unsubscribe());
    }
}

const globalEventBus = window[GLOBAL_KEY] || (window[GLOBAL_KEY] = new EventBus());

const GLOBAL_EVENTS = {
    TAB_VISIBILITY_CHANGED: 'tabVisibilityChanged',
    BUILD_IS_STALE: 'build_is_stale',

    UPDATE_CONVERSATIONS_COUNTERS: 'update_conversations_counters',
    UPDATE_TASKS_COUNTERS: 'update_tasks_counters',
    UPDATE_ACTIVITY_COUNTERS: 'update_activity_counters',
    CONVERSATIONS_UNREAD_COUNT_CHANGED: 'conversations_unread_count_changed',
    CONVERSATIONS_UNREAD_COUNT_INCREASED: 'conversations_unread_count_increased',
    ACTIVITY_UNREAD_COUNT_INCREASED: 'activity_unread_count_increased',
    TASK_COUNT_CHANGED: 'task_count_changed',

    MY_AVATAR_CHANGED: 'my_avatar_changed',
    PROFILE_DATA_CHANGED: 'profile_data_changed',
    ACCOUNT_DELETION_CANCELLED: 'accountDeletionCancelled',

    ENABLE_FOOTER_LOGIN_MODE: 'enableFooterLoginMode',
    DISABLE_FOOTER_LOGIN_MODE: 'disableFooterLoginMode',

    ADD_BUTTON_TO_GROUP_HEADER: 'ADD_BUTTON_TO_GROUP_HEADER',
    REMOVE_BUTTON_FROM_GROUP_HEADER: 'REMOVE_BUTTON_FROM_GROUP_HEADER',
};

export default globalEventBus;
export {globalEventBus, GLOBAL_EVENTS, EventBus};
